/**
 * What a commission's or an oath's record says about its document (P-20, A-#244; Arthur, 28 October 2026). The file's
 * name was printed as plain text, which looked clickable and was not. Now: a document the reader may open is a
 * "View document" button opening the Documents section's own audited preview; anything else is said in words.
 *
 * The preview reads the file through the Documents section's routes (uploads.ts, documents.ts: requireSection
 * 'documents'), and a Clio copy opens only to a holder of the documents module (132), so the button is offered only to
 * a reader who holds Documents. Another reader is told a document is on file, never given a control that would refuse.
 */

export interface RecordDocumentFields {
  upload_id: string | null;
  upload_name: string | null;
  upload_status: string | null;
  clio_copy_id: string | null;
}

export type RecordDocumentState =
  | { kind: 'view'; source: 'upload'; id: string; name: string }
  | { kind: 'view'; source: 'clio'; id: string }
  | { kind: 'words'; words: string };

export const NO_DOCUMENT = 'No document yet. Add one from the full view';
export const NO_DOCUMENT_HERE = 'No document yet';
export const BEING_CHECKED = 'Being checked for viruses';
export const DID_NOT_PASS = 'Did not pass the virus check';
export const ON_FILE = 'A document is on file, opened from the client’s Documents section';

/** `none`: the words for no document; the record's own panel says NO_DOCUMENT_HERE, since it is the full view. */
export function recordDocumentState(r: RecordDocumentFields | null, holdsDocuments: boolean, none = NO_DOCUMENT): RecordDocumentState {
  if (r?.upload_id) {
    if (r.upload_status === 'awaiting_scan') return { kind: 'words', words: BEING_CHECKED };
    if (r.upload_status !== 'clean') return { kind: 'words', words: DID_NOT_PASS };
    if (!holdsDocuments) return { kind: 'words', words: ON_FILE };
    return { kind: 'view', source: 'upload', id: r.upload_id, name: r.upload_name ?? 'The document' };
  }
  if (r?.clio_copy_id) {
    if (!holdsDocuments) return { kind: 'words', words: ON_FILE };
    return { kind: 'view', source: 'clio', id: r.clio_copy_id };
  }
  return { kind: 'words', words: none };
}
